import { Video } from 'lucide-react'
import type { TimelineItem } from '@/types/timeline'
import type { TaskContext } from '@/types/task'
import type { FamilyMember } from '@/types/family'
import { useScheduleActionsContext } from '@/contexts/ScheduleActionsContext'
import { ContextPicker } from '@/components/triage'
import { AssigneeDropdown, MultiAssigneeDropdown } from '@/components/family'
import { RoutineMoveButton } from './RoutineMoveButton'
import { SnoozeButton } from './SnoozeButton'
import { RAIL, RAIL_SLOT, RAIL_WHO } from './todayRowGrid'

interface RowActionRailProps {
  item: TimelineItem
  onContextChange?: (context: TaskContext | undefined) => void
  onAssign?: (memberId: string | null) => void
  onAssignAll?: (memberIds: string[]) => void
  familyMembers: FamilyMember[]
  assignedTo?: string | null
  assignedToAll: string[]
  /** Someone is assigned — you included. Then the avatars stay lit at rest:
   *  who carries it is information (Scott, 2026-10-04: "why are some …
   *  avatars only appearing on hover even though they've been assigned?").
   *  Only an unassigned row's picker waits under hover with the controls. */
  assigned?: boolean
}

/** An icon cell — 28px, on every row, always. */
const SLOT = RAIL_SLOT

/**
 * The assignee cell is wider than an icon cell because the avatar stack grows
 * with the number of people: MultiAssigneeDropdown draws up to four 24px
 * circles at -4px overlap (enough to read every initial), so 24px for one
 * and 84px for four. Reserving the
 * maximum is what stops that growth shoving its neighbours around as the
 * assignees change — the exact bug this rail exists to kill. Because EVERY
 * cell is a reserved fixed width, the slot order below is free: it is a
 * reading decision, not a layout constraint.
 *
 * Right-aligned inside the cell (not centred) so the stack sits flush against
 * the context icon that follows it. The reserve then spends itself as
 * whitespace on the cell's LEFT, where it merges with the title column's own
 * slack and reads as the gap before the rail rather than a hole inside it.
 */
const WHO_SLOT = RAIL_WHO

/**
 * Controls are quiet until you reach for them.
 *
 * Individually each rail glyph is defensible; collectively, four of them on
 * every row of the day is what stops the page reading as text. So the default
 * state is editorial — the title, the time and who it involves — and the
 * things you ACT with fade in on hover or keyboard focus.
 *
 * Reserved widths are what make this safe: the cell keeps its box whether or
 * not its contents are painted, so revealing the rail moves nothing. Hiding a
 * control by unmounting it would reintroduce exactly the jitter this rail was
 * built to kill.
 *
 * md: only — a touch device has no hover to reveal anything with, so mobile
 * keeps every control painted.
 */
const QUIET = 'transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100'

// Start Meeting button - uses context to avoid prop drilling
function StartMeetingButton({ item }: { item: TimelineItem }) {
  const ctx = useScheduleActionsContext()
  const onStartMeeting = ctx.onStartMeeting

  if (!onStartMeeting) return null

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const eventId = item.id.replace('event-', '')
    onStartMeeting(
      eventId,
      item.title,
      item.attendees || [],
      item.startTime ?? undefined,
      item.endTime ?? undefined
    )
  }

  return (
    <button
      onClick={handleClick}
      className="p-1.5 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
      title="Start meeting"
      aria-label="Start meeting"
    >
      <Video className="w-4 h-4" />
    </button>
  )
}

/**
 * The row's trailing controls, as a FIXED four-cell rail.
 *
 * Every cell is 28px and every gap is 4px on every row, whatever the row's
 * type — empty cells render as spacers rather than collapsing. That is the
 * whole point. These controls used to be six conditional flex siblings, so a
 * task showed six, an event five, a routine three, and because the title
 * column absorbed the slack, only the last one ever formed a column. Nothing
 * down the page lined up. Reserve the shape and the columns hold.
 *
 * Slot order is fixed: assignee | context | verb | spare. At most one verb
 * exists per row type — a task snoozes, a routine moves or skips this
 * occurrence, a timed event starts — so they share one slot without ever
 * competing for it.
 *
 * Today, trimmed (2026-10-14) took the task's Reschedule button and the row ⋯
 * menu away; their jobs live in the item panel (TapContextPanel /
 * TapEventPanel). Snooze came back on its own (2026-10-27) — the one move you
 * make from the list without opening anything. The last cell stays as a
 * reserved spacer: RoutineCollectionRow draws the same four cells, so the
 * people column still lines up down the page.
 *
 * The order reads WHO -> WHAT KIND -> DO IT -> MORE: identity and
 * classification first, because "is this mine?" is the question you ask of a
 * whole list at once and it wants a column near the text; the two action
 * controls sit outboard, where a mis-aimed tap is least costly. Any order is
 * equally stable — every cell is a reserved width — so this is chosen for
 * reading, not for layout.
 *
 * The rail carries ACTIONS only. State (flagged for discussion, subtask
 * counts) belongs with the title, because a strip that means two things can't
 * have one shape.
 */
export function RowActionRail({
  item,
  onContextChange,
  onAssign,
  onAssignAll,
  familyMembers,
  assignedTo,
  assignedToAll,
  assigned = true,
}: RowActionRailProps) {
  const isTask = item.type === 'task'
  const isRoutine = item.type === 'routine'
  const isEvent = item.type === 'event'
  const isLive = !item.completed && !item.skipped

  const verb =
    isTask && isLive ? <SnoozeButton item={item} />
    : isRoutine && isLive ? <RoutineMoveButton item={item} />
    : isEvent && isLive && !item.allDay ? <StartMeetingButton item={item} />
    : null

  // Context is hover-revealed in BOTH states now. It was already hidden while
  // unset (the tag-needs-context nudge); the set state is a small coloured
  // diamond that reads as decoration at a glance and costs a row of noise, and
  // the domain a row belongs to is answered far better by the layer lens than
  // by squinting at a 12px chip.
  const context = onContextChange ? (
    <div
      className={QUIET}
      onClick={(e) => e.stopPropagation()}
    >
      <ContextPicker
        size="sm"
        value={item.context ?? undefined}
        onChange={onContextChange}
      />
    </div>
  ) : null

  const who = familyMembers.length > 0 && onAssignAll ? (
    <div onClick={(e) => e.stopPropagation()}>
      <MultiAssigneeDropdown
        members={familyMembers}
        selectedIds={assignedToAll}
        onSelect={onAssignAll}
        size="sm"
        label={isEvent ? "Who's attending?" : "Who's responsible?"}
      />
    </div>
  ) : familyMembers.length > 0 && onAssign ? (
    <div onClick={(e) => e.stopPropagation()}>
      <AssigneeDropdown
        members={familyMembers}
        selectedId={assignedTo}
        onSelect={onAssign}
        size="sm"
      />
    </div>
  ) : null

  // assignee | context | verb | spare — see the slot-order note above.
  // `who` is the one cell that stays lit: who a thing involves is household
  // INFORMATION, not a control, and it is half the reason to look at the row
  // at all. Everything after it is something you do, not something you read.
  const cells: Array<{ node: React.ReactNode; className: string; quiet?: boolean }> = [
    // …unless nobody is assigned: then it is only the picker, a control, and
    // it waits with the others.
    { node: who, className: WHO_SLOT, quiet: !assigned },
    { node: context, className: SLOT },
    { node: verb, className: SLOT, quiet: true },
    // Reserved, empty: keeps this rail as wide as RoutineCollectionRow's.
    { node: null, className: SLOT },
  ]

  return (
    <div className={RAIL}>
      {cells.map((cell, i) => (
        <div key={i} data-rail-slot className={`${cell.className}${cell.quiet ? ` ${QUIET}` : ''}`}>
          {cell.node}
        </div>
      ))}
    </div>
  )
}
